import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Square, RotateCcw, Upload, Camera, CameraOff, X, Monitor, PictureInPicture2
} from "lucide-react";
import { toast } from "sonner";

interface Props {
  onVideoReady: (blob: Blob, filename: string) => void;
  onCancel: () => void;
  maxDurationSeconds?: number;
}

type SourceMode = "camera" | "screen" | "pip";

export default function VideoRecorder({ onVideoReady, onCancel, maxDurationSeconds = 600 }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pipVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const camStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);

  const [status, setStatus] = useState<"idle" | "previewing" | "recording" | "recorded" | "error">("idle");
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [sourceMode, setSourceMode] = useState<SourceMode>("camera");
  const [pipPosition, setPipPosition] = useState<"bottom-left" | "bottom-right" | "top-left" | "top-right">("bottom-left");

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const stopAllStreams = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    camStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    camStreamRef.current = null;
    screenStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    if (pipVideoRef.current) pipVideoRef.current.srcObject = null;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = null;
  }, []);

  // Draw PiP composite on canvas
  const drawPipFrame = useCallback(() => {
    const canvas = canvasRef.current;
    const screenVideo = videoRef.current;
    const camVideo = pipVideoRef.current;
    if (!canvas || !screenVideo || !camVideo) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 1280;
    canvas.height = 720;

    // Draw screen
    ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height);

    // Draw camera PiP
    const pipW = 240;
    const pipH = 180;
    const margin = 16;
    let px: number, py: number;
    switch (pipPosition) {
      case "bottom-right": px = canvas.width - pipW - margin; py = canvas.height - pipH - margin; break;
      case "top-left": px = margin; py = margin; break;
      case "top-right": px = canvas.width - pipW - margin; py = margin; break;
      default: px = margin; py = canvas.height - pipH - margin;
    }

    // Rounded rect clip
    const r = 12;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(px + r, py);
    ctx.lineTo(px + pipW - r, py);
    ctx.quadraticCurveTo(px + pipW, py, px + pipW, py + r);
    ctx.lineTo(px + pipW, py + pipH - r);
    ctx.quadraticCurveTo(px + pipW, py + pipH, px + pipW - r, py + pipH);
    ctx.lineTo(px + r, py + pipH);
    ctx.quadraticCurveTo(px, py + pipH, px, py + pipH - r);
    ctx.lineTo(px, py + r);
    ctx.quadraticCurveTo(px, py, px + r, py);
    ctx.closePath();
    ctx.clip();

    // Mirror camera
    ctx.translate(px + pipW, py);
    ctx.scale(-1, 1);
    ctx.drawImage(camVideo, 0, 0, pipW, pipH);
    ctx.restore();

    // Border
    ctx.strokeStyle = "rgba(255,255,255,0.6)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px + r, py);
    ctx.lineTo(px + pipW - r, py);
    ctx.quadraticCurveTo(px + pipW, py, px + pipW, py + r);
    ctx.lineTo(px + pipW, py + pipH - r);
    ctx.quadraticCurveTo(px + pipW, py + pipH, px + pipW - r, py + pipH);
    ctx.lineTo(px + r, py + pipH);
    ctx.quadraticCurveTo(px, py + pipH, px, py + pipH - r);
    ctx.lineTo(px, py + r);
    ctx.quadraticCurveTo(px, py, px + r, py);
    ctx.closePath();
    ctx.stroke();

    animFrameRef.current = requestAnimationFrame(drawPipFrame);
  }, [pipPosition]);

  const startCamera = useCallback(async () => {
    try {
      if (sourceMode === "pip") {
        // Get screen first
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: true,
        });
        screenStreamRef.current = screenStream;

        // Get camera
        const camStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 320 }, height: { ideal: 240 } },
          audio: true,
        });
        camStreamRef.current = camStream;

        // Set video sources for canvas drawing
        if (videoRef.current) {
          videoRef.current.srcObject = screenStream;
          videoRef.current.muted = true;
          await videoRef.current.play();
        }
        if (pipVideoRef.current) {
          pipVideoRef.current.srcObject = camStream;
          pipVideoRef.current.muted = true;
          await pipVideoRef.current.play();
        }

        // Start canvas compositing
        animFrameRef.current = requestAnimationFrame(drawPipFrame);

        // Create combined stream from canvas + audio
        const canvas = canvasRef.current!;
        const canvasStream = canvas.captureStream(30);

        // Add audio tracks from both streams
        const audioCtx = new AudioContext();
        const dest = audioCtx.createMediaStreamDestination();
        screenStream.getAudioTracks().forEach((t) => {
          const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
          source.connect(dest);
        });
        camStream.getAudioTracks().forEach((t) => {
          const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
          source.connect(dest);
        });
        dest.stream.getAudioTracks().forEach((t) => canvasStream.addTrack(t));

        streamRef.current = canvasStream;

        // Handle screen share stop
        screenStream.getVideoTracks()[0].onended = () => {
          if (mediaRecorderRef.current?.state === "recording") {
            stopRecording();
          } else {
            stopAllStreams();
            setStatus("idle");
          }
        };

        setStatus("previewing");
      } else if (sourceMode === "screen") {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: true,
        });
        screenStreamRef.current = screenStream;

        // Mix system audio + mic audio using AudioContext
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          camStreamRef.current = micStream;

          const audioCtx = new AudioContext();
          const dest = audioCtx.createMediaStreamDestination();

          // Add system audio tracks
          screenStream.getAudioTracks().forEach((t) => {
            const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
            source.connect(dest);
          });

          // Add mic audio tracks
          micStream.getAudioTracks().forEach((t) => {
            const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
            source.connect(dest);
          });

          // Create combined stream: screen video + mixed audio
          const combinedStream = new MediaStream();
          screenStream.getVideoTracks().forEach((t) => combinedStream.addTrack(t));
          dest.stream.getAudioTracks().forEach((t) => combinedStream.addTrack(t));
          streamRef.current = combinedStream;
        } catch {
          // No mic available, use screen stream as-is
          streamRef.current = screenStream;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = screenStream;
          videoRef.current.muted = true;
          await videoRef.current.play();
        }
        screenStream.getVideoTracks()[0].onended = () => {
          if (mediaRecorderRef.current?.state === "recording") {
            stopRecording();
          } else {
            stopAllStreams();
            setStatus("idle");
          }
        };
        setStatus("previewing");
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          await videoRef.current.play();
        }
        setStatus("previewing");
      }
    } catch (err) {
      console.error("Media error:", err);
      toast.error("تعذر الوصول إلى الوسائط المطلوبة");
      setStatus("error");
    }
  }, [sourceMode, drawPipFrame, stopAllStreams]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : "video/webm";

    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setRecordedUrl(url);
      setStatus("recorded");
      stopAllStreams();
    };

    recorder.start(1000);
    setStatus("recording");
    setElapsed(0);

    timerRef.current = setInterval(() => {
      setElapsed((prev) => {
        if (prev + 1 >= maxDurationSeconds) {
          stopRecording();
          return prev + 1;
        }
        return prev + 1;
      });
    }, 1000);
  }, [maxDurationSeconds, stopAllStreams]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const resetRecording = useCallback(() => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsed(0);
    setStatus("idle");
  }, [recordedUrl]);

  const handleSubmit = useCallback(() => {
    if (!recordedBlob) return;
    const filename = `شرح-درس-${Date.now()}.webm`;
    onVideoReady(recordedBlob, filename);
  }, [recordedBlob, onVideoReady]);

  useEffect(() => {
    return () => {
      stopAllStreams();
      if (timerRef.current) clearInterval(timerRef.current);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, []);

  const modeLabels: Record<SourceMode, { icon: typeof Camera; label: string; startLabel: string }> = {
    camera: { icon: Camera, label: "الكاميرا", startLabel: "بدء الكاميرا" },
    screen: { icon: Monitor, label: "الشاشة", startLabel: "بدء المشاركة" },
    pip: { icon: PictureInPicture2, label: "شاشة + كاميرا", startLabel: "بدء التسجيل المركب" },
  };

  const currentMode = modeLabels[sourceMode];
  const ModeIcon = currentMode.icon;

  return (
    <div className="space-y-3">
      {/* Source mode selector */}
      {status === "idle" && (
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          {(Object.entries(modeLabels) as [SourceMode, typeof currentMode][]).map(([key, val]) => {
            const Icon = val.icon;
            return (
              <Button
                key={key}
                variant={sourceMode === key ? "default" : "outline"}
                size="sm"
                onClick={() => setSourceMode(key)}
                className="gap-1.5 text-xs"
              >
                <Icon className="w-3.5 h-3.5" />
                {val.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Video Preview */}
      <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
        {status === "recorded" && recordedUrl ? (
          <video src={recordedUrl} controls className="w-full h-full object-contain" />
        ) : sourceMode === "pip" && (status === "previewing" || status === "recording") ? (
          <>
            {/* Hidden source videos for canvas drawing */}
            <video ref={videoRef} className="hidden" playsInline muted />
            <video ref={pipVideoRef} className="hidden" playsInline muted />
            {/* Canvas shows the composite */}
            <canvas ref={canvasRef} className="w-full h-full object-contain" />
          </>
        ) : (
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            style={sourceMode === "camera" ? { transform: "scaleX(-1)" } : undefined}
            playsInline
          />
        )}

        {status === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white gap-3">
            <ModeIcon className="w-12 h-12 opacity-60" />
            <p className="text-sm opacity-80">اضغط على "{currentMode.startLabel}"</p>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white gap-3">
            <CameraOff className="w-12 h-12 opacity-60" />
            <p className="text-sm opacity-80">تعذر الوصول</p>
          </div>
        )}

        {status === "recording" && (
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-destructive/90 text-white px-2.5 py-1 rounded-full text-xs font-medium">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              تسجيل
            </div>
            <Badge variant="secondary" className="bg-black/60 text-white border-0 text-xs">
              {formatTime(elapsed)}
            </Badge>
          </div>
        )}

        {/* PiP position control */}
        {sourceMode === "pip" && (status === "previewing" || status === "recording") && (
          <div className="absolute top-3 left-3 flex gap-1">
            {(["top-left", "top-right", "bottom-left", "bottom-right"] as const).map((pos) => (
              <button
                key={pos}
                onClick={() => setPipPosition(pos)}
                className={`w-5 h-5 rounded border grid grid-cols-2 grid-rows-2 gap-px p-0.5 transition-colors ${
                  pipPosition === pos ? "border-white bg-white/20" : "border-white/40 bg-black/30 hover:bg-white/10"
                }`}
                title={pos}
              >
                {["top-left", "top-right", "bottom-left", "bottom-right"].map((cell) => (
                  <div
                    key={cell}
                    className={`rounded-[1px] ${cell === pos ? "bg-white" : "bg-white/20"}`}
                  />
                ))}
              </button>
            ))}
          </div>
        )}

        {status === "recording" && (
          <div className="absolute bottom-0 left-0 right-0">
            <Progress value={(elapsed / maxDurationSeconds) * 100} className="h-1 rounded-none [&>div]:bg-destructive" />
          </div>
        )}
      </div>

      {/* Hidden elements for PiP mode */}
      {sourceMode !== "pip" && (
        <>
          <video ref={pipVideoRef} className="hidden" playsInline muted />
          <canvas ref={canvasRef} className="hidden" />
        </>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {status === "idle" && (
          <>
            <Button onClick={startCamera} className="gap-1.5">
              <ModeIcon className="w-4 h-4" />
              {currentMode.startLabel}
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="w-4 h-4" />
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Button onClick={startCamera} variant="outline" className="gap-1.5">
              <RotateCcw className="w-4 h-4" />
              إعادة المحاولة
            </Button>
            <Button variant="ghost" onClick={onCancel}>إلغاء</Button>
          </>
        )}

        {status === "previewing" && (
          <>
            <Button onClick={startRecording} variant="destructive" className="gap-1.5">
              <div className="w-3 h-3 rounded-full bg-white" />
              بدء التسجيل
            </Button>
            <Button variant="outline" onClick={() => { stopAllStreams(); setStatus("idle"); }}>
              إلغاء
            </Button>
          </>
        )}

        {status === "recording" && (
          <Button onClick={stopRecording} variant="destructive" className="gap-1.5">
            <Square className="w-4 h-4" />
            إيقاف التسجيل
          </Button>
        )}

        {status === "recorded" && (
          <>
            <Button onClick={handleSubmit} className="gap-1.5">
              <Upload className="w-4 h-4" />
              رفع الفيديو
            </Button>
            <Button variant="outline" onClick={resetRecording} className="gap-1.5">
              <RotateCcw className="w-4 h-4" />
              إعادة التسجيل
            </Button>
            <Button variant="ghost" onClick={onCancel}>إلغاء</Button>
          </>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground text-center">
        الحد الأقصى: {Math.floor(maxDurationSeconds / 60)} دقائق • يتم التسجيل بصيغة WebM
        {recordedBlob && ` • الحجم: ${(recordedBlob.size / (1024 * 1024)).toFixed(1)} MB`}
      </p>
    </div>
  );
}
